import { message } from 'antd';
import axios, { AxiosInstance } from 'axios';
import qs from 'qs';

let unauthorizedRedirectInProgress = false;

class AxiosHttpClient {
  protected readonly instance: AxiosInstance;

  public constructor(baseURL?: string, authenticated = true) {
    this.instance = axios.create({
      baseURL: process.env.REACT_APP_URL_API,
      withCredentials: true,
      paramsSerializer: (p) => qs.stringify(p)
    });
    axios.defaults.withCredentials = true;
    if (authenticated) {
      this.instance.interceptors.request.use(async (config) => {
        const token = process.env.REACT_APP_TOKEN_API?.trim();
        // Only attach Authorization when a token is explicitly provided.
        if (token) {
          config.headers = config.headers ?? {};
          config.headers.Authorization = token;
        }
        return config;
      });
      this.instance.interceptors.response.use(
        (response) => {
          return response;
        },
        async (error) => {
          const errorCodes = [401];
          const status = error?.response?.status;
          const errorMessage = error?.response?.data?.message || error?.message;

          if (errorMessage) {
            message.error(errorMessage);
          }

          if (error && errorCodes.includes(status)) {
            const apiBase = process.env.REACT_APP_URL_API?.replace(/\/+$/, '') || window.location.origin;
            const loginPath = '/system/login';
            const loginUrl = `${apiBase}${loginPath}`;
            const isAlreadyOnLogin = window.location.pathname.startsWith(loginPath);

            // Prevent infinite redirect loops when multiple requests fail with 401 simultaneously.
            if (!isAlreadyOnLogin && !unauthorizedRedirectInProgress) {
              unauthorizedRedirectInProgress = true;
              window.location.assign(loginUrl);
            }
          }
          return Promise.reject(error);
        }
      );
    }
  }

  public async get<T>(url: string, query?: any, headers: any = {}): Promise<T> {
    const response = await this.instance?.get<T>(url, {
      params: query,
      headers
    });
    return response.data;
  }

  public async post<T>(url: string, body: unknown): Promise<T> {
    const response = await this.instance.post<T>(url, body);
    return response.data;
  }

  public async delete<T>(url: string): Promise<T> {
    const response = await this.instance.delete<T>(url);
    return response.data;
  }

  public async put<T>(url: string, body: unknown): Promise<T> {
    const response = await this.instance.put<T>(url, body);
    return response.data;
  }
}

export default AxiosHttpClient;
